import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ColumnDef,
  getCoreRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { Info } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { displayToastSuccess } from '@/helpers/display-toast';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { DeleteRoleModal } from '@/modules/settings/components/roles/delete-role-modal';
import { RolesTable } from '@/modules/settings/components/roles/roles-table';
import { getRolesColumns } from '@/modules/settings/constants/roles-table-columns';
import { useRoles } from '@/modules/settings/hooks/roles/use-roles';
import { useUpdateRoles } from '@/modules/settings/hooks/roles/use-update-roles';
import { RoleFormData } from '@/modules/settings/types/role';
import { RolesFormData, rolesFormSchema } from '@/modules/settings/validations/role-validation';
import { SettingsRoutes } from '@/router/routes';
import { compareFormValues } from '@/utils/form-state.utils';

import { SettingsPageWrapper } from '../../components/settings-page-wrapper';
import { SettingsOutletContext } from '../../types/settings-footer';

let nextTempId = -1;

export const RolesPage = () => {
  const { t } = useTranslation('authenticated.settings');
  const navigate = useNavigate();
  const { setFooterState } = useOutletContext<SettingsOutletContext>();

  const { data: roles, isLoading, error, refetch } = useRoles();
  const updateRolesMutation = useUpdateRoles();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<RoleFormData | null>(null);

  // Track initial data to compare against
  const initialDataRef = useRef<RoleFormData[]>([]);

  // Helper function to map roles from API to form data
  const mapRolesToFormData = useCallback((rls: typeof roles): RoleFormData[] => {
    return rls
      ? rls.map(role => ({
          roleId: role.id,
          name: role.name,
          memberCount: role.memberCount,
        }))
      : [];
  }, []);

  // Initialize form default values
  const defaultValues = useMemo(() => {
    return { roles: mapRolesToFormData(roles) };
  }, [roles, mapRolesToFormData]);

  const form = useForm<RolesFormData>({
    resolver: zodResolver(rolesFormSchema),
    mode: 'onChange',
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'roles',
  });

  // Hook to guard against navigation when there are unsaved changes
  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty: form.formState.isDirty,
    onDiscard: () => form.reset(),
  });

  // Store initial data when roles load
  useEffect(() => {
    if (roles) {
      const formData = mapRolesToFormData(roles);
      initialDataRef.current = formData;
      form.reset({ roles: formData });
    }
  }, [roles, form, mapRolesToFormData]);

  const handleSave = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const formData = form.getValues();

    // Prepare roles for update (filter out empty names)
    const rolesToUpdate = formData.roles
      .filter(role => role.name.trim().length > 0)
      .map(role => ({
        id: role.roleId > 0 ? role.roleId : undefined,
        name: role.name.trim(),
      }));

    try {
      await updateRolesMutation.mutateAsync(rolesToUpdate);
      // Refetch to get updated data with new IDs
      await refetch();
      displayToastSuccess(t('pages.roles.saveSuccess'));
    } catch {
      // Error handled by mutation
    }
  }, [form, updateRolesMutation, refetch, t]);

  // Compare current state with initial using utility
  const hasActualChanges = useCallback(() => {
    const currentValues = form.getValues().roles;
    const initialValues = initialDataRef.current;
    return compareFormValues(currentValues, initialValues);
  }, [form]);

  // Update footer state - simple direct effect
  useEffect(() => {
    const hasChanges = hasActualChanges();
    const isValid = form.formState.isValid;

    setFooterState({
      isVisible: true,
      isEnabled: hasChanges && isValid,
      isLoading: updateRolesMutation.isPending,
      onSave: handleSave,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.formState.isValid,
    form.formState.isDirty, // Trigger when form changes
    updateRolesMutation.isPending,
    fields.length, // Trigger when roles added/removed
  ]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      setFooterState({
        isVisible: false,
        isEnabled: false,
        isLoading: false,
        onSave: () => {},
      });
    };
  }, [setFooterState]);

  const handleAddRole = () => {
    const newRole: RoleFormData = {
      roleId: nextTempId--,
      name: '',
      memberCount: 0,
    };
    append(newRole);
  };

  const handleDeleteClick = useCallback(
    (role: RoleFormData, index: number) => {
      // For new roles (client-side only with negative IDs), just remove from form
      if (role.roleId < 0) {
        remove(index);
        return;
      }

      // For existing roles, check if there are unsaved changes
      const shouldProceed = unsavedChangesGuard.checkUnsavedChanges(() => {
        // This callback executes after user discards changes
        setRoleToDelete(role);
        setDeleteModalOpen(true);
      });

      if (shouldProceed) {
        // Form is clean, proceed immediately with delete
        setRoleToDelete(role);
        setDeleteModalOpen(true);
      }
    },
    [remove, unsavedChangesGuard]
  );

  const handleDeleteSuccess = async () => {
    setDeleteModalOpen(false);
    setRoleToDelete(null);
    // Refetch data and reset form to clean state
    await refetch();
  };

  const columns = useMemo<ColumnDef<RoleFormData>[]>(
    () =>
      getRolesColumns({
        onDelete: handleDeleteClick,
        control: form.control,
        t,
        navigate,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const table = useReactTable({
    data: fields,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  } as TableOptions<RoleFormData>);

  const unsavedChangesGuardProps = useMemo(
    () => ({
      isModalOpen: unsavedChangesGuard.isModalOpen,
      onDiscard: unsavedChangesGuard.handleModalDiscard,
      onCancel: unsavedChangesGuard.handleModalCancel,
    }),
    [
      unsavedChangesGuard.isModalOpen,
      unsavedChangesGuard.handleModalDiscard,
      unsavedChangesGuard.handleModalCancel,
    ]
  );

  return (
    <SettingsPageWrapper
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        void refetch();
      }}
      unsavedChangesGuard={unsavedChangesGuardProps}
    >
      <Card className="p-6">
        {/* Card Header */}
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold leading-7">{t('pages.roles.title')}</h2>
          <Button variant="ghost" className="hidden h-9 px-3 py-2 text-sm font-medium">
            {t('pages.roles.configureRates')}
          </Button>
        </div>

        {/* Description */}
        <p className="mb-4 text-xs text-muted-foreground">{t('pages.roles.description')}</p>

        {/* Info Alert */}
        <Alert variant="info" className="mb-4">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <Trans
              i18nKey="pages.roles.infoAlert"
              t={t}
              components={{
                permissionLink: (
                  <Link
                    to={SettingsRoutes.Permissions}
                    className="font-bold underline hover:opacity-80"
                  />
                ),
              }}
            />
          </AlertDescription>
        </Alert>

        {/* Roles Count */}
        <p className="mb-4 text-xs text-muted-foreground">
          {t('pages.roles.allRoles', { count: roles?.length || 0 })}
        </p>

        {/* Table */}
        <Form {...form}>
          <form>
            <RolesTable
              table={table}
              onAddRole={handleAddRole}
              hasRoles={fields.length > 0}
              isLoading={isLoading}
            />
          </form>
        </Form>
      </Card>

      {/* Delete Role Modal */}
      <DeleteRoleModal
        role={roleToDelete}
        allRoles={roles || []}
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setRoleToDelete(null);
        }}
        onSuccess={handleDeleteSuccess}
      />
    </SettingsPageWrapper>
  );
};
